/**
 * ClassCardData — normalized display type for all class card variants
 *
 * Adapter functions convert from various source types:
 * - ClassWithProfile (profile pages, browse, discovery)
 * - StudioClassWithDetails (studio management views)
 * - UserRegistrationWithClass (registered classes)
 * - SeriesListItem (series cards)
 */

import type {
  ClassStatus,
  ClassWithProfile,
  ClassData,
  CollaboratorProfileInfo,
} from "@/lib/api/classes";
import type { UserRegistrationWithClass } from "@/lib/api/registrations";
import type { StudioClassWithDetails } from "@dnceclub/shared";
import {
  getInstructorDisplayInfo,
  type InstructorDisplayInfo,
} from "@/lib/utils/class-instructor-utils";
import { getRoomDisplay } from "@/lib/utils/class-room-utils";
import {
  formatSeriesDateCompact,
  formatClassTimeRangeCompact,
} from "@/lib/utils/class-time-utils";
import type { SeriesListItem } from "./class-list";

// ---------------------------------------------------------------------------
// ClassCardData interface
// ---------------------------------------------------------------------------

export interface ClassCardData {
  id: string;
  type: "single" | "series";
  href: string;
  title: string;
  status: ClassStatus;
  photoUrl: string | null;
  photoObjectPosition?: string;
  startTime: Date;
  endTime: Date;
  timezone: string;
  customDateTimeString?: string;
  location: string;
  address?: string | null;
  room?: string | null;
  studio?: {
    id: string;
    name: string;
    handle: string | null;
    logo_url: string | null;
  } | null;
  price: number | null;
  customPriceString?: string;
  instructor: InstructorDisplayInfo;
  collaborators: CollaboratorProfileInfo[];
  isCollaborativeClass: boolean;
  ownerProfile?: CollaboratorProfileInfo | null;
  /**
   * Current user's registration state for this class, when known. Surfaces a
   * REGISTERED / UNPAID badge on the card. Omit/null when the viewer isn't
   * signed in or the registration lookup hasn't completed.
   */
  userRegistration?: {
    isPaid: boolean;
    isOnWaitlist: boolean;
  } | null;
}

// ---------------------------------------------------------------------------
// Type guards
// ---------------------------------------------------------------------------

function isUserRegistration(source: unknown): source is UserRegistrationWithClass {
  return (
    typeof source === "object" &&
    source !== null &&
    ("class_id" in source || ("class" in source && "is_on_waitlist" in source))
  );
}

function isStudioClass(source: unknown): source is StudioClassWithDetails {
  return (
    typeof source === "object" &&
    source !== null &&
    "rate_info" in source &&
    !("collaborators" in source)
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Build a local Date from "YYYY-MM-DD" + "HH:MM" or "HH:MM:SS". Returns Invalid Date safely if inputs are missing. */
function buildDateTime(dateStr: string | null | undefined, timeStr: string | null | undefined): Date {
  if (!dateStr || !timeStr) return new Date(NaN);
  return new Date(`${dateStr}T${timeStr}`);
}

/** Format sliding scale price range: "$10 \u2013 $25" */
function formatSlidingScalePrice(
  minPrice: number | null,
  maxPrice: number | null
): string | undefined {
  if (minPrice != null && maxPrice != null) {
    return `$${minPrice} \u2013 $${maxPrice}`;
  }
  return undefined;
}

/** Get price range string from series pricing tiers: "$25 - $80" */
function getSeriesPriceRange(
  pricing: { class_count: number; price: number }[]
): { price: number | null; customPriceString?: string } {
  if (pricing.length === 0) {
    return { price: null };
  }

  const prices = pricing.map((p) => p.price);
  const min = Math.min(...prices);
  const max = Math.max(...prices);

  if (min === max) {
    return { price: min, customPriceString: `$${min}` };
  }

  return { price: min, customPriceString: `$${min} - $${max}` };
}

// ---------------------------------------------------------------------------
// Photo resolution helpers
// ---------------------------------------------------------------------------

/** Resolve photo for ClassWithProfile / ClassData */
function resolveClassWithProfilePhoto(
  source: ClassData & { profile?: { profile_photo?: string | null } | null }
): { photoUrl: string | null; photoObjectPosition?: string } {
  // 1. Cover photo
  if (source.cover_photo_url) {
    return { photoUrl: source.cover_photo_url };
  }

  // 2. Room photo (studio-managed classes)
  if (source.is_studio_managed && source.room_info?.photos?.[0]) {
    return { photoUrl: source.room_info.photos[0] };
  }

  // 3. Series host's profile photo (for series classes — matches class-detail page)
  const seriesHostPhoto = source.series?.profile?.profile_photo;
  if (seriesHostPhoto) {
    return { photoUrl: seriesHostPhoto, photoObjectPosition: "center 20%" };
  }

  // 4. Class instructor's profile photo (with adjusted position for face centering)
  const profilePhoto = source.profile?.profile_photo;
  if (profilePhoto) {
    return { photoUrl: profilePhoto, photoObjectPosition: "center 20%" };
  }

  return { photoUrl: null };
}

/** Resolve photo for StudioClassWithDetails — room photo only */
function resolveStudioClassPhoto(
  source: StudioClassWithDetails
): { photoUrl: string | null; photoObjectPosition?: string } {
  // Room photo only — studio classes don't use cover or profile photos
  const roomPhotos = (
    source.room_info as { photos?: string[] | null } | null | undefined
  )?.photos;
  if (roomPhotos?.[0]) {
    return { photoUrl: roomPhotos[0] };
  }

  return { photoUrl: null };
}

/** Resolve photo for UserRegistrationWithClass (nested class) */
function resolveRegistrationPhoto(cls: {
  cover_photo_url: string | null;
  profile?: { profile_photo?: string | null } | null;
  series?: {
    profile?: { profile_photo?: string | null } | null;
  } | null;
}): { photoUrl: string | null; photoObjectPosition?: string } {
  // 1. Cover photo
  if (cls.cover_photo_url) {
    return { photoUrl: cls.cover_photo_url };
  }

  // 2. Series host's profile photo (for series classes)
  const seriesHostPhoto = cls.series?.profile?.profile_photo;
  if (seriesHostPhoto) {
    return { photoUrl: seriesHostPhoto, photoObjectPosition: "center 20%" };
  }

  // 3. Class instructor's profile photo
  const profilePhoto = cls.profile?.profile_photo;
  if (profilePhoto) {
    return { photoUrl: profilePhoto, photoObjectPosition: "center 20%" };
  }

  return { photoUrl: null };
}

// ---------------------------------------------------------------------------
// Adapter: ClassWithProfile -> ClassCardData
// ---------------------------------------------------------------------------

function fromClassWithProfile(source: ClassWithProfile): ClassCardData {
  const { photoUrl, photoObjectPosition } = resolveClassWithProfilePhoto(source);

  // Backend attaches display_status at the route layer (`withDisplayStatus`).
  const status = source.display_status;

  // substitute_profile may exist at runtime (e.g. from discover endpoint) but isn't in the ClassData type
  const substituteProfile = (source as unknown as Record<string, unknown>).substitute_profile as
    | { id: string; username?: string | null; full_name?: string | null; profile_photo?: string | null }
    | undefined;

  const instructor = getInstructorDisplayInfo(
    source.profile,
    source.guest_instructor_name,
    substituteProfile,
    source.substitute_guest_name,
  );

  const room = getRoomDisplay(
    source.room,
    source.room_paid_only,
    undefined, // isPaid
    undefined, // canManage
    source.is_studio_managed,
  );

  const customPriceString =
    source.pricing_type === "sliding_scale"
      ? formatSlidingScalePrice(source.min_price, source.max_price)
      : undefined;

  return {
    id: source.id,
    type: "single",
    href: `/classes/${source.id}`,
    title: source.title,
    status,
    photoUrl,
    photoObjectPosition,
    startTime: buildDateTime(source.date, source.start_time),
    endTime: buildDateTime(source.date, source.end_time),
    timezone: source.timezone,
    location: source.location,
    address: source.address,
    room,
    studio: source.studio_info ?? null,
    price: source.price,
    customPriceString,
    instructor,
    collaborators: source.collaborators ?? [],
    isCollaborativeClass: !!source.isCollaborator,
    ownerProfile: source.ownerProfile ?? null,
  };
}

// ---------------------------------------------------------------------------
// Adapter: StudioClassWithDetails -> ClassCardData
// ---------------------------------------------------------------------------

function fromStudioClass(source: StudioClassWithDetails): ClassCardData {
  const { photoUrl, photoObjectPosition } = resolveStudioClassPhoto(source);

  const instructor = getInstructorDisplayInfo(
    source.profile,
    source.guest_instructor_name,
    source.substitute_profile,
    source.substitute_guest_name,
  );

  const price = source.rate_info?.price ?? source.price;

  return {
    id: source.id,
    type: "single",
    href: `/classes/${source.id}`,
    title: source.title,
    status: source.status as ClassStatus,
    photoUrl,
    photoObjectPosition,
    startTime: buildDateTime(source.date, source.start_time),
    endTime: buildDateTime(source.date, source.end_time),
    timezone: source.timezone,
    location: source.location,
    address: source.address,
    room: source.room_info?.name,
    studio: null,
    price,
    instructor,
    collaborators: [],
    isCollaborativeClass: false,
  };
}

// ---------------------------------------------------------------------------
// Adapter: UserRegistrationWithClass -> ClassCardData
// ---------------------------------------------------------------------------

function fromRegistration(source: UserRegistrationWithClass): ClassCardData {
  const cls = source.class;
  if (!cls) {
    throw new Error("UserRegistrationWithClass.class is null — cannot convert to ClassCardData");
  }

  const { photoUrl, photoObjectPosition } = resolveRegistrationPhoto(cls);

  const instructor = getInstructorDisplayInfo(
    cls.profile,
    // UserRegistrationClassData does NOT have guest_instructor_name or substitute fields
    (cls as unknown as Record<string, unknown>).guest_instructor_name as string | undefined,
    (cls as unknown as Record<string, unknown>).substitute_profile as Record<string, unknown> | undefined,
    (cls as unknown as Record<string, unknown>).substitute_guest_name as string | undefined,
  );

  const room = getRoomDisplay(
    cls.room,
    cls.room_paid_only,
    source.is_paid,
    undefined, // canManage
  );

  const customPriceString =
    cls.pricing_type === "sliding_scale"
      ? formatSlidingScalePrice(cls.min_price, cls.max_price)
      : undefined;

  // Extract studio info if present (added by getUserRegistrations join)
  const studioInfo = (cls as unknown as Record<string, unknown>).studio as
    | { id: string; name: string; handle: string | null; logo_url: string | null }
    | null
    | undefined;

  return {
    id: cls.id,
    type: "single",
    href: `/classes/${cls.id}`,
    title: cls.title,
    status: cls.status as ClassStatus,
    photoUrl,
    photoObjectPosition,
    startTime: buildDateTime(cls.date, cls.start_time),
    endTime: buildDateTime(cls.date, cls.end_time),
    timezone: cls.timezone,
    location: cls.location,
    address: cls.address,
    room,
    studio: studioInfo ?? null,
    price: cls.price,
    customPriceString,
    instructor,
    collaborators: (cls.collaborators ?? []) as CollaboratorProfileInfo[],
    isCollaborativeClass: false,
  };
}

// ---------------------------------------------------------------------------
// Public API: toClassCardData (polymorphic)
// ---------------------------------------------------------------------------

/**
 * Convert any class source type to the normalized ClassCardData.
 *
 * Type detection order:
 * 1. UserRegistrationWithClass — has `class_id` or (`class` + `is_on_waitlist`)
 * 2. StudioClassWithDetails — has `rate_info` key and no `collaborators`
 * 3. ClassWithProfile — everything else (default)
 *
 * When `userRegistration` is provided, it's attached to the result so the
 * card can render a REGISTERED / UNPAID status badge. Use
 * `buildUserRegistrationLookup` to build the lookup once per list.
 */
export function toClassCardData(
  source: ClassWithProfile | StudioClassWithDetails | UserRegistrationWithClass,
  userRegistration?: { isPaid: boolean; isOnWaitlist: boolean } | null,
): ClassCardData {
  const base = isUserRegistration(source)
    ? fromRegistration(source)
    : isStudioClass(source)
      ? fromStudioClass(source)
      : fromClassWithProfile(source as ClassWithProfile);

  // If caller didn't pass a registration but the source is itself a user
  // registration, derive the status from it.
  if (userRegistration === undefined && isUserRegistration(source)) {
    const reg = source as UserRegistrationWithClass;
    return {
      ...base,
      userRegistration: {
        isPaid: reg.is_paid,
        isOnWaitlist: reg.is_on_waitlist,
      },
    };
  }

  return userRegistration !== undefined
    ? { ...base, userRegistration }
    : base;
}

// ---------------------------------------------------------------------------
// Public API: seriesToClassCardData
// ---------------------------------------------------------------------------

/**
 * Convert a SeriesListItem to ClassCardData.
 *
 * Series cards display differently — they use customDateTimeString instead of
 * individual date/time, and pricing comes from the pricing tiers.
 */
export function seriesToClassCardData(
  source: SeriesListItem,
  options?: { dateOnly?: boolean },
): ClassCardData {
  const instructor = getInstructorDisplayInfo(source.profile);

  const { price, customPriceString } = getSeriesPriceRange(source.pricing);

  // Build time from schedule or fallback fields
  const firstDate = source.classSchedule?.[0]?.date ?? source.firstClassDate ?? "1970-01-01";
  const firstStart = source.classSchedule?.[0]?.start_time ?? source.firstClassStartTime ?? "00:00";
  const firstEnd = source.classSchedule?.[0]?.end_time ?? source.firstClassEndTime ?? "00:00";

  // Build customDateTimeString
  const customDateTimeString = options?.dateOnly
    ? buildSeriesDateOnlyString(source)
    : buildSeriesDateTimeString(source);

  return {
    id: source.id,
    type: "series",
    href: `/series/${source.id}`,
    title: source.title,
    status: source.status as ClassStatus,
    photoUrl: source.coverPhotoUrl,
    startTime: buildDateTime(firstDate, firstStart),
    endTime: buildDateTime(firstDate, firstEnd),
    timezone: source.timezone,
    customDateTimeString,
    location: source.location,
    address: source.address,
    room: source.room,
    studio: null,
    price,
    customPriceString,
    instructor,
    collaborators: (source.collaborators ?? []) as CollaboratorProfileInfo[],
    isCollaborativeClass: false,
  };
}

// ---------------------------------------------------------------------------
// Series date/time string builder
// ---------------------------------------------------------------------------

/** Date-only display for profile series cards: "MAR 1, MAR 8, MAR 15" */
function buildSeriesDateOnlyString(source: SeriesListItem): string | undefined {
  const schedule = source.classSchedule;

  if (schedule && schedule.length > 0) {
    return schedule.map((s) => formatSeriesDateCompact(s.date)).join(", ");
  }

  // Fallback: use firstClassDate / lastClassDate
  if (source.firstClassDate) {
    if (source.lastClassDate && source.lastClassDate !== source.firstClassDate) {
      return `${formatSeriesDateCompact(source.firstClassDate)} \u2013 ${formatSeriesDateCompact(source.lastClassDate)}`;
    }
    return formatSeriesDateCompact(source.firstClassDate);
  }

  return undefined;
}

function buildSeriesDateTimeString(source: SeriesListItem): string | undefined {
  const schedule = source.classSchedule;

  if (schedule && schedule.length > 0) {
    // Use the class schedule for a rich display
    // Format: "APR 1 – APR 22 · 7PM-8:30PM" (multi-day) or "APR 18 · 7PM-8:30PM" (single-day)
    const first = schedule[0];
    const last = schedule[schedule.length - 1];
    const timeRange = formatClassTimeRangeCompact(first.start_time, first.end_time);
    const dateRange =
      first.date === last.date
        ? formatSeriesDateCompact(first.date)
        : `${formatSeriesDateCompact(first.date)} \u2013 ${formatSeriesDateCompact(last.date)}`;

    return `${dateRange} \u00B7 ${timeRange}`;
  }

  // Fallback: use firstClassDate / lastClassDate
  if (source.firstClassDate && source.firstClassStartTime && source.firstClassEndTime) {
    const timeRange = formatClassTimeRangeCompact(source.firstClassStartTime, source.firstClassEndTime);

    if (source.lastClassDate && source.lastClassDate !== source.firstClassDate) {
      const dateRange = `${formatSeriesDateCompact(source.firstClassDate)} \u2013 ${formatSeriesDateCompact(source.lastClassDate)}`;
      return `${dateRange} \u00B7 ${timeRange}`;
    }

    return `${formatSeriesDateCompact(source.firstClassDate)} \u00B7 ${timeRange}`;
  }

  return undefined;
}
